import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  Flex,
  Box,
  Editable,
  EditablePreview,
  EditableInput,
  useEditableControls,
} from '@chakra-ui/react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { colors } from 'packages/common-frontend/colors';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  StrictFieldValues,
  StringPath,
} from '~/core/routes/settings/implementations/common/Inputs/types';

const EditableBody = () => {
  const { getEditButtonProps } = useEditableControls();
  return (
    <Flex gap="1rem" alignItems="center">
      <EditablePreview />
      <EditableInput />
      <IconButtonV2
        {...getEditButtonProps()}
        aria-label="Edit"
        colorScheme="gray"
        icon={<Icon icon="edit" />}
        variant="v2-ghost"
      />
    </Flex>
  );
};

type EditableTitleProps<T extends StrictFieldValues> = {
  name: StringPath<T>;
  placeholder?: string;
  required?: boolean;
  helperText?: React.ReactNode;
};

/**
 * An editable title component that displays as a large heading and can be edited inline by clicking the edit icon.
 *
 * @template T - The form data type that extends StrictFieldValues
 *
 * Props:
 * - `name`: Field name in the form. Must be a string field
 * - `placeholder`: Optional placeholder text shown when the field is empty. Defaults to "Enter title"
 * - `required`: When true, field is required and shows validation error if empty
 * - `helperText`: Optional helper text displayed below the editable title
 *
 * @example
 * // Basic editable title
 * <EditableTitle<FormData> name="projectTitle" />
 *
 * @example
 * // Required title with custom placeholder and helperText
 * <EditableTitle<FormData>
 *   name="documentTitle"
 *   placeholder="Untitled Document"
 *   helperText="This title will appear on the cover page"
 *   required
 * />
 */
export const EditableTitle = <T extends StrictFieldValues>({
  name,
  placeholder = 'Enter title',
  required,
  helperText,
}: EditableTitleProps<T>) => {
  const { control } = useFormContext<T>();

  const rules = required
    ? {
        required: { value: true, message: 'This is a required field' },
      }
    : undefined;

  return (
    <Box flexShrink="0">
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <FormControl isInvalid={!!error}>
            <Editable
              display="flex"
              value={field.value as string}
              onChange={(newValue) => field.onChange(newValue)}
              onBlur={field.onBlur}
              placeholder={placeholder}
              style={{
                color: field.value ? 'black' : colors.gray[300], // workaround to placeholder styling
                fontSize: '2.25rem',
                fontWeight: 'bold',
                lineHeight: '2.6875rem',
              }}
            >
              <EditableBody />
            </Editable>
            {error && <FormErrorMessage>{error.message}</FormErrorMessage>}
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
          </FormControl>
        )}
      />
    </Box>
  );
};
